/**
 * The generated blank (docs/ux/matter-hub.md addendum, ADR-0001): the
 * registry's word on generating for this doctor and type, Generate as a
 * job, the version built and the Word download. Refusals show the server's
 * sentence; a running job polls until it lands.
 */

import { DownloadIcon } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { useIsPhone } from '@/app/viewport';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { ReadinessChip } from '@/features/registry/ReadinessChip';
import { dateTime } from '@/lib/format';
import { Panel } from '../panels';
import { generateRefusal, useGenerateBlank, useReport } from './useReport';

const kb = (bytes: number): string => `${Math.max(1, Math.round(bytes / 1024))} KB`;

export function BlankPanel({ matterRef, offline }: { matterRef: string; offline: boolean }) {
  const phone = useIsPhone();
  const report = useReport(matterRef);
  const generate = useGenerateBlank(matterRef);
  const [refusal, setRefusal] = useState<string | null>(null);
  if (report.isPending) return <Panel title="Generated blank"><Skeleton className="h-24 rounded-lg" /></Panel>;
  if (report.isError || !report.data) return <Panel title="Generated blank"><p className="text-sm text-beam-error">The blank's state could not be loaded.</p></Panel>;
  const v = report.data;
  const running = v.head?.build?.state === 'running' || v.head?.build?.state === 'queued';
  const failed = v.head?.build?.state === 'failed';
  const blank = v.head?.blank ?? null;

  async function onGenerate() {
    setRefusal(null);
    try {
      await generate.mutateAsync();
      toast.success('Generating the blank.');
    } catch (err) {
      setRefusal(generateRefusal(err));
    }
  }

  return (
    <Panel title="Generated blank" action={v.canGenerate ? (
      <Button size={phone ? 'lg' : 'sm'} className={phone ? 'h-11' : undefined} data-slot="generate-blank" data-primary={blank ? undefined : 'true'} disabled={offline || running || generate.isPending} aria-busy={running || generate.isPending} onClick={() => void onGenerate()}>
        {running || generate.isPending ? 'Generating' : blank ? 'Generate again' : 'Generate blank'}
      </Button>
    ) : undefined}>
      <div className="flex flex-col gap-3 text-sm">
        {v.readiness ? (
          <p className="flex flex-wrap items-center gap-2" data-slot="report-readiness">
            <ReadinessChip outcome={v.readiness.outcome} size="sm" />
            <span className="text-beam-text-muted">{v.readiness.sentence}</span>
          </p>
        ) : null}
        {!v.canGenerate && v.reason ? <p data-slot="generate-refused" className="text-beam-text-muted">Generation is refused: {v.reason}</p> : null}
        {refusal ? <p role="alert" className="text-beam-error">{refusal}</p> : null}
        {failed ? <p role="alert" className="text-beam-error">The last generation failed; try again.</p> : null}
        {blank ? (
          <div className="flex flex-col gap-1" data-slot="blank-version" data-version={blank.version}>
            <span className="font-medium">Version {blank.version}, built {dateTime(blank.builtAt)}, {kb(blank.bytes)}.</span>
            <a href={`/api/matters/${encodeURIComponent(matterRef)}/report/blank`} download={blank.name} data-slot="download-blank" className={phone ? 'inline-flex min-h-11 w-fit items-center gap-1.5 text-sm underline-offset-2 hover:underline' : 'inline-flex w-fit items-center gap-1.5 text-sm underline-offset-2 hover:underline'}>
              <DownloadIcon aria-hidden="true" className="size-4" />Download {blank.name}
            </a>
            <span className="text-[13px] text-beam-text-muted">{v.statutory ? 'The official APIA1 certificate with the case\'s, the letter\'s, the booking\'s and the registry\'s details filled in; the assessment cells are the workspace\'s boxes.' : 'The doctor\'s headings, the standing text and the letter\'s questions in the house layout; the workspace is the same structure on screen.'}</span>
          </div>
        ) : (
          <p className="text-beam-text-muted">{v.canGenerate ? (v.statutory ? 'No blank generated yet. The blank is the official APIA1 certificate prefilled with what the case knows, for a doctor who fills the certificate outside the workspace.' : 'No blank generated yet. The blank is the doctor\'s structure as a Word file, for a doctor who drafts outside the workspace or for the Quality Scribe.') : ''}</p>
        )}
      </div>
    </Panel>
  );
}
